'use client';
import type { FitRow } from '@/lib/research/types';

interface Props {
  fit: FitRow | null;
  inPortfolio: boolean;
}

export function PortfolioFitPanel({ fit, inPortfolio }: Props) {
  if (!fit) {
    return (
      <div className="text-[12px] text-[#64748b]">
        Portfolio-fit data unavailable for this symbol.
      </div>
    );
  }

  const { betaSpy, maxCorr, topCorrelations, sectorConcDelta, flags } = fit;
  const fmtNum = (v: number | null, digits = 2) => v == null ? '—' : v.toFixed(digits);
  const fmtSigned = (v: number | null, digits = 2) =>
    v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(digits)}`;

  return (
    <div className="space-y-3 text-[12px]">
      {inPortfolio ? (
        <div className="text-amber-400 text-[11px] font-semibold tracking-wide uppercase">
          Already in portfolio
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <Stat label="β vs SPY" value={fmtNum(betaSpy)} />
        <Stat
          label="Sector Δ"
          value={fmtSigned(sectorConcDelta, 1)}
          unit="pp"
          warn={flags.sectorOverweight}
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[10px] text-[#64748b] uppercase tracking-wide">Correlations to every holding</div>
          <div className="text-[10px] text-[#64748b] tabular-nums">
            {topCorrelations.length} {topCorrelations.length === 1 ? 'name' : 'names'}
          </div>
        </div>
        {topCorrelations.length === 0 ? (
          <div className="text-[#94a3b8]">No portfolio overlap.</div>
        ) : (
          <ul className="grid grid-cols-3 gap-x-4 gap-y-0.5 max-h-56 overflow-y-auto pr-1">
            {topCorrelations.map(({ symbol, corr }) => {
              const isMax = maxCorr?.symbol === symbol;
              const isHigh = corr >= 0.85;
              const isMod = corr >= 0.5 && !isHigh;
              const isNeg = corr < 0;
              const tone = isHigh ? 'text-amber-400 font-semibold'
                : isMod ? 'text-orange-300'
                : isNeg ? 'text-emerald-400'
                : 'text-[#94a3b8]';
              const symTone = isHigh ? 'text-amber-400' : 'text-[#e2e8f0]';
              return (
                <li key={symbol} className="flex justify-between items-baseline gap-3 text-[11px] tabular-nums">
                  <span className={`font-mono ${symTone}`}>{symbol}</span>
                  <span className={tone}>
                    {corr.toFixed(2)}{isMax ? ' ←' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {flags.highlyCorrelated && !inPortfolio ? (
        <div className="text-[11px] text-amber-400 leading-snug">
          Adds little: max corr {maxCorr?.value.toFixed(2)} with {maxCorr?.symbol} (already in book).
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, unit, warn = false }: { label: string; value: string; unit?: string; warn?: boolean }) {
  return (
    <div>
      <div className="text-[10px] text-[#64748b] uppercase tracking-wide">{label}</div>
      <div className={`text-[14px] font-semibold tabular-nums ${warn ? 'text-amber-400' : 'text-white'}`}>
        {value}{unit ? <span className="text-[10px] text-[#64748b] ml-1">{unit}</span> : null}
      </div>
    </div>
  );
}
